import { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Search, CheckCheck, Trash2, MoreVertical, Bell,
  CheckCircle2, XCircle, Award, Users, Gift, Banknote, PartyPopper, Frown,
} from "lucide-react";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";
import {
  listNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  deleteReadNotifications,
} from "@/lib/afritask";

const TABS = [
  { key: "new", label: "Unread" },
  { key: "viewed", label: "Viewed" },
  { key: "all", label: "All" },
];

// Icon + circle-tint per type category (subject/preview lives in the row).
const TYPE_ICON = {
  // Money — emerald
  withdrawal_paid: { icon: Banknote, tint: "bg-emerald-100 text-emerald-700" },
  withdrawal_approved: { icon: Banknote, tint: "bg-emerald-100 text-emerald-700" },
  withdrawal_failed: { icon: XCircle, tint: "bg-red-100 text-red-700" },
  submission_approved: { icon: CheckCircle2, tint: "bg-emerald-100 text-emerald-700" },
  submission_rejected: { icon: XCircle, tint: "bg-red-100 text-red-700" },
  // Wins / completion — amber
  selection_won: { icon: PartyPopper, tint: "bg-amber-100 text-amber-700" },
  selection_missed: { icon: Frown, tint: "bg-neutral-100 text-neutral-600" },
  task_lost: { icon: Frown, tint: "bg-neutral-100 text-neutral-600" },
  task_cancelled: { icon: XCircle, tint: "bg-amber-100 text-amber-700" },
  badge_earned: { icon: Award, tint: "bg-amber-100 text-amber-700" },
  // Referrals — amber (with emerald for the money portion)
  referral_signup: { icon: Users, tint: "bg-amber-100 text-amber-700" },
  referral_topup_bonus: { icon: Gift, tint: "bg-amber-100 text-amber-700" },
  referral_revenue_share: { icon: Banknote, tint: "bg-emerald-100 text-emerald-700" },
  welcome_bonus: { icon: Gift, tint: "bg-amber-100 text-amber-700" },
};

// Relative timestamp: "2h ago", "Yesterday", "Jul 10", "Jul 10 2025".
const fmtRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const diffSec = Math.round((now - d) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  const sameYear = d.getFullYear() === now.getFullYear();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, sameYear
    ? { day: "numeric", month: "short" }
    : { day: "numeric", month: "short", year: "numeric" });
};

// Full timestamp for the detail drawer.
const fmtWhen = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

export default function MessageCentre() {
  const { me } = useOutletContext();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("new");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const load = async () => {
    try {
      const d = await listNotifications(100);
      setItems(d.notifications || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (me) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.user_id]);

  // Auto-open a notification from ?open={id}
  useEffect(() => {
    const openId = params.get("open");
    if (openId && items.length > 0) {
      const n = items.find((x) => x.notification_id === openId);
      if (n) {
        openMessage(n);
        setParams({}, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, params]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    let list = items;
    if (tab === "new") list = list.filter((n) => !n.read);
    else if (tab === "viewed") list = list.filter((n) => n.read);
    if (query) list = list.filter((n) => (n.title + " " + (n.body || "")).toLowerCase().includes(query));
    return list;
  }, [items, tab, q]);

  const unreadCount = items.filter((n) => !n.read).length;
  const readCount = items.filter((n) => n.read).length;

  const openMessage = async (n) => {
    setDetail(n);
    if (!n.read) {
      await markNotificationRead(n.notification_id).catch(() => {});
      await load();
    }
  };

  const gotoTarget = (n) => {
    if (!n) return;
    if (n.task_id && (n.type === "selection_won" || n.type === "submission_approved" || n.type === "submission_rejected")) {
      navigate(`/app/tasks/${n.task_id}/workspace`);
    } else if ((n.type || "").startsWith("withdrawal_")) {
      navigate("/app/earnings");
    } else if (n.type === "badge_earned") {
      navigate("/app/profile");
    } else if ((n.type || "").startsWith("referral_")) {
      navigate("/app/referrals");
    } else if (n.task_id) {
      navigate(`/app/tasks/${n.task_id}`);
    }
  };

  const markAll = async () => {
    setBusy(true);
    try { await markAllNotificationsRead(); await load(); toast.success("Marked all as read"); }
    finally { setBusy(false); }
  };
  const clearRead = async () => {
    setConfirmClearOpen(true);
  };
  const doClearRead = async () => {
    setConfirmClearOpen(false);
    setBusy(true);
    try {
      const r = await deleteReadNotifications();
      await load();
      toast.success(`Deleted ${r.deleted} read messages`);
    } finally { setBusy(false); }
  };
  const del = async (n) => {
    setBusy(true);
    try { await deleteNotification(n.notification_id); await load(); }
    finally { setBusy(false); }
  };
  const toggleRead = async (n) => {
    // Only mark as read supported by backend; unread flip is client-side sugar
    if (!n.read) {
      await markNotificationRead(n.notification_id).catch(() => {});
      await load();
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Inbox</h1>
          <p className="mt-0.5 text-xs text-neutral-500">
            {unreadCount} unread · {readCount} viewed
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={markAll} disabled={busy || unreadCount === 0}
                  data-testid="mc-mark-all">
            <CheckCheck className="mr-1 h-3.5 w-3.5" /> Mark all read
          </Button>
          <Button variant="outline" size="sm" onClick={clearRead} disabled={busy || readCount === 0}
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  data-testid="mc-delete-read">
            <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete read
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search subject or body…" className="pl-10"
               data-testid="mc-search" />
      </div>

      {/* Tabs — pill segmented control */}
      <div
        className="grid grid-cols-3 gap-1 rounded-full bg-neutral-100 p-1"
        data-testid="mc-tabs"
      >
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`inline-flex h-9 items-center justify-center gap-1.5 rounded-full text-sm font-medium transition ${
                active
                  ? "bg-[#B45309] text-white shadow-sm"
                  : "text-neutral-700 hover:bg-neutral-200"
              }`}
              data-testid={`mc-tab-${t.key}`}
            >
              {t.label}
              {t.key === "new" && unreadCount > 0 && (
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                    active ? "bg-white/25 text-white" : "bg-[#B45309] text-white"
                  }`}
                >
                  {unreadCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Message list — flat card with hairline dividers */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <EmptyState
              art="envelope"
              title={tab === "new" ? "You're all caught up" : tab === "viewed" ? "No viewed messages" : "You have no messages yet"}
              body={tab === "new" ? "No new messages — we'll let you know when something happens." : ""}
              testid="mc-empty-state"
            />
          </CardContent>
        </Card>
      ) : (
        <div
          className="divide-y divide-neutral-100 overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm"
          data-testid="mc-list"
        >
          {filtered.map((n) => {
            const meta = TYPE_ICON[n.type] || { icon: Bell, tint: "bg-neutral-100 text-neutral-600" };
            const Icon = meta.icon;
            return (
              <div
                key={n.notification_id}
                className={`group flex items-start gap-3 px-4 py-3 ${
                  n.read ? "" : "bg-amber-50/30"
                }`}
                data-testid={`mc-row-${n.notification_id}`}
              >
                {/* Unread dot */}
                <div className="mt-2 w-2 shrink-0">
                  {!n.read && (
                    <span
                      className="block h-2 w-2 rounded-full bg-[#B45309]"
                      data-testid={`mc-unread-dot-${n.notification_id}`}
                      aria-label="Unread"
                    />
                  )}
                </div>
                {/* Type icon */}
                <span
                  className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${meta.tint}`}
                  data-testid={`mc-icon-${n.notification_id}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                </span>
                {/* Subject + preview (clickable) */}
                <button
                  type="button"
                  onClick={() => openMessage(n)}
                  className="min-w-0 flex-1 text-left"
                  data-testid={`mc-open-${n.notification_id}`}
                >
                  <p
                    className={`truncate text-sm ${
                      n.read ? "text-neutral-700" : "font-semibold text-neutral-900"
                    }`}
                  >
                    {n.title}
                  </p>
                  {n.body && (
                    <p className="mt-0.5 line-clamp-1 text-xs text-neutral-500">
                      {n.body}
                    </p>
                  )}
                </button>
                {/* Timestamp */}
                <span
                  className="mt-0.5 whitespace-nowrap text-xs text-neutral-500"
                  title={fmtWhen(n.created_at)}
                  data-testid={`mc-time-${n.notification_id}`}
                >
                  {fmtRelative(n.created_at)}
                </span>
                {/* Actions */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="rounded p-1 text-neutral-400 opacity-100 hover:bg-neutral-100 hover:text-neutral-700 sm:opacity-0 sm:group-hover:opacity-100"
                      data-testid={`mc-menu-${n.notification_id}`}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {!n.read && (
                      <DropdownMenuItem onClick={() => toggleRead(n)}>
                        <CheckCircle2 className="mr-2 h-3.5 w-3.5" />
                        Mark as read
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => del(n)} className="text-red-600">
                      <Trash2 className="mr-2 h-3.5 w-3.5" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail drawer */}
      <Sheet open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg" data-testid="mc-detail-drawer">
          <SheetHeader>
            <SheetTitle>{detail?.title || "Message"}</SheetTitle>
          </SheetHeader>
          {detail && (
            <div className="mt-6 space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-500">
                <span>{fmtWhen(detail.created_at)}</span>
                <span>· AfriTask AI</span>
              </div>
              <p className="text-sm leading-relaxed text-neutral-800 whitespace-pre-wrap">
                {detail.body}
              </p>
              {(detail.task_id ||
                (detail.type || "").startsWith("withdrawal_") ||
                (detail.type || "").startsWith("referral_")) && (
                <Button onClick={() => { const n = detail; setDetail(null); gotoTarget(n); }}
                        className="bg-amber-700 hover:bg-amber-800"
                        data-testid="mc-detail-cta">
                  View details →
                </Button>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmClearOpen} onOpenChange={setConfirmClearOpen}>
        <AlertDialogContent data-testid="mc-clear-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete all read messages?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove every message you&apos;ve already opened.
              This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mc-clear-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doClearRead}
              className="bg-red-600 hover:bg-red-700"
              data-testid="mc-clear-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
